// The main store used for tracking purposes

// Helpers
import {DateTime as dt} from "luxon";
import mainMixins from "../../../mainMixins";
import skuList from "../sku";

export default {
	namespaced: true,
	state: () => ({ }),
	getters: {
        // Traversing the cart data to pull out the selected items
        impactCart(state, getters, rootState){
            const data = rootState.Buyflow.receipt.cartData
            const flattenedItems = [];

            // Helper function to extract items from the nested structures
            function extractItems(items, category) {
                if (Array.isArray(items)) {
                    items.forEach(item => {
                        if (item.name && item.promoPrice !== undefined) {
                            const { name, quantity, promoPrice } = item;
                            let newItem = { sku: skuList[name] || name.replace(' ', ''), name: name, subTotal: promoPrice.toString(), quantity: '1', category: category };
                            if (quantity !== undefined) {
                                newItem.quantity = quantity.toString();
                            }

                            flattenedItems.push(newItem);
                        }
                    });
                }
            }

            // Process 'services' section
            Object.entries(data.services).forEach(([category, service]) => {
                if (Array.isArray(service)) {
                    extractItems(service, category);
                }
            });
            return flattenedItems;
        },
        personalInformation(state, getters, rootState) { //returns checkout form data to be sent to salesforce
            let data = rootState.meta
            
            let identities = Object.entries({
                houseKeyNew: data.locationId,
                emailAddress: data.emailAddress,
            }).reduce((availableIdentities, [key, value]) => {
                if (value) availableIdentities[key] = value;
                    return availableIdentities;
            }, {});
            
            // Use reducer to return only non-empty values
            let attributes = Object.entries({
                houseKeyNew: data.locationId,
                emailAddress: data.emailAddress,
                firstName: data.firstName,
                lastName: data.lastName,
                phoneNumber: data.phoneNumber,
            }).reduce((availableAttributes, [key, value]) => {
                if (value) availableAttributes[key] = value;
                    return availableAttributes;
            }, {});
        
            return { identities, attributes }
        },
		salesforceCart(state, getters, rootState) { // returns the cart data in a format that can be sent to salesforce
			function flattenServicesAndAddOns(data) {
				const flattenedItems = [];

				// Helper function to extract items from the nested structures
				function extractItems(items) {
					if (Array.isArray(items)) {
						items.forEach(item => {
							if (item.name && item.promoPrice !== undefined) {
								const { name, quantity, promoPrice } = item;
								let newItem = { _id: skuList[name] || name.replace(' ', ''), price: promoPrice.toString(), quantity: '1' };
								if (quantity !== undefined) {
									newItem.quantity = quantity.toString();
								}
								flattenedItems.push(newItem);
							}
						});
					}
				}

				// Process 'services' section
				Object.values(data.services).forEach(service => {
					if (Array.isArray(service)) {
						extractItems(service);
					}
				});

				// Process 'addOns' section
				Object.values(data.addOns).forEach(addOn => {
					if (typeof addOn === 'object' && !Array.isArray(addOn)) {
						Object.values(addOn).forEach(addOnType => extractItems(addOnType));
					} else if (Array.isArray(addOn)) {
						extractItems(addOn);
					}
				});

				return flattenedItems;
			}
			const cartData = rootState.Buyflow.receipt.cartData
			return flattenServicesAndAddOns(cartData)
		},
	},
	actions: {
		updatePersonalizationCart({ dispatch, rootState, rootGetters, getters }) { // updates the cart selections in personalization
			/* START Personalization */
			const cartData = rootState.Buyflow.receipt.cartData
			let internet = cartData?.services?.internet.length > 0? cartData?.services?.internet[0]?.name: rootState?.selectedOffer?.name ?? ''

            const personalInfo = getters.personalInformation;
            const { attributes: personalAttributes, identities: personalIdentities } = personalInfo;

            let userAttributes = {
                cartDate: dt.now().toString(),
                cartURL: rootGetters['Buyflow/cartUrl'],
                cartItem: (function () {
                    let name = [];
                    name.push((internet).replace(/ Internet|Unlimited /g, ''));
                    cartData?.addOns.internet.equipment.length > 1 ? name.push('WiFi Your Way&trade; Home') : null;
                    cartData?.services.tv.length ? name.push('TV') : null;
                    cartData?.services.voice.length ? name.push('Voice') : null;
        
                    return name.join(' + ');
                })(),
                cartItemPrice: (rootGetters.isFmf ? 
                    (cartData.promoPriceTotal + rootGetters.fmfAmount).toFixed(2).toString() : 
                    cartData.promoPriceTotal.toFixed(2).toString()),
            };
            
            // Merge personal information with cart 
            userAttributes = { ...userAttributes, ...personalAttributes };

			dispatch('Personalization/ping', {
				eventName: 'VIEW_UPDATE_CART',
				payload: {
                    user: {
                        identities: personalIdentities,
                        attributes: userAttributes
                    },
					source: {
						pageType: 'cart',
						url: window.location.href,
						urlReferrer: document.referrer,
						locale: 'en_US',
					},
					cart: {
						complete: {
							Product: getters.salesforceCart,
						}
					},
				},
			}, ROOT)
			/* END Personalization */
		},
		trackCompletedOrder({ dispatch, getters, rootState, rootGetters }) { // tracks the completed order
			const summary = rootState.Buyflow.summary.summary

			if (Object.keys(summary).length > 0) {
                const tv = rootGetters['Buyflow/tv/hasSelectedAService']
                const voice = rootGetters['Buyflow/voice/hasSelected']()
                const internet = rootGetters['Buyflow/internet/selectedService'] || null
                const equipment = rootGetters['Buyflow/internet/isEquipmentSelected']({name: 'Customer Owned Modem'})
                const safariAgent = (navigator.userAgent.indexOf("Safari") > -1 && navigator.userAgent.indexOf("Chrome") === -1);

                window.scroll({
                    top: 0,
                    left: 0,
                    behavior: safariAgent ? 'auto' : 'smooth',
                })

                if (rootState.meta.referralCodeUsed && rootState.meta.referralCode) {
                    mainMixins.sendEvent('checkout', 'summary', 'referral code completed sale')
                }

                dispatch('Buyflow/setSessionStatus', {session_status: 5}, ROOT)
                dispatch('Buyflow/setCurrentStep', [5, 'summary page'], ROOT)
                dispatch('setEcommerceItem', {
                    item: {
                        name: internet.name,
                        brand: "Breezeline Online",
                        category: `internet${!equipment ? '-equipment' : ''}${!!tv ? '-tv' : ''}${!!voice ? '-voice' : ''}`,
                        dimension1: internet.speed.download, // download speed
                        dimension2: null, // channel count TODO update this to an actual count
                        dimension3: !!tv, // TV Added
                        dimension4: !!voice, // Voice Added
                        dimension5: !equipment, // Wifi Your Way Added
                        dimension6: rootGetters['Buyflow/summary/formatInstallDate'](), // Installation Date
                        price: rootState.Buyflow.receipt.cartData.promoPriceTotal,
                        metric1: rootState.Buyflow.receipt.cartData.fullPriceTotal, // full price
                    }
                }, ROOT)
                window.dataLayer = window.dataLayer || []
                window.dataLayer.push({
                    event: 'sale_conversion',
                    cookie_state: rootState.meta.stateOrProvince || null,
                    cookie_zip_code: rootState.meta.postalCode ? rootState.meta.postalCode.substring(0, 5) : null,
                    order: {
                        id: rootState.meta.uuid,
                        accountNumber: summary.id.replace(/#/g, ''),
                        promoDuration: rootState.Buyflow.receipt.cartData.promoDuration,
                        installationDate: rootGetters['Buyflow/summary/formatInstallDate'](),
                        taxRate: rootState.Buyflow.receipt.cartData.taxes,
                        revenue: (rootState.Buyflow.receipt.cartData.promoPriceTotal + rootState.Buyflow.receipt.cartData.taxes).toFixed(2),
                        subscriptionRevenue: rootState.ecommerceItem.fullPrice,
                        service: rootState.ecommerceItem,
                    },
                })
                gtag("event", "purchase", {
                    transaction_id: summary.id.replace(/#/g, ''),
                    affiliation: "Breezeline Online",
                    currency: "USD",
                    value: rootState.Buyflow.receipt.cartData.promoPriceTotal + rootState.Buyflow.receipt.cartData.taxes,
                    tax: rootState.Buyflow.receipt.cartData.taxes,
                    items: [rootState.ecommerceItem],
                })

                if (rootState.Buyflow.mobileEligible) {
                    mainMixins.sendEvent('', '', '', `mobile_eligible_and_${rootState.Buyflow.mobileInterested?'interested':'not_interested'}_bzlsale`)
                } else {
                    mainMixins.sendEvent('', '', '', `mobile_ineligible_and_${rootState.Buyflow.mobileInterested?'interested':'not_interested'}_bzlsale`)
                }

                if (rootState.meta.acsid) {
                    mainMixins.sendEvent('', "User comes from AllConnect and converts", rootState.meta.acsid, 'all_connect_conversion')
                }

                // Personalization base user attributes
                let userAttributes = {
                    accountId: summary.id.replace(/#/g, ''),
                    cartURL: '',
                    cartItem: '',
                    cartItemPrice: '',
                    cartDate: '',
                    allConnectId: rootState.meta.acsid,
                };

                // When we have a click ID, we can send the conversion event details to Personalization:
                if (rootState.meta.impactRadiusClickId) {
                    console.log('Impact Radius: Sending conversion event to Personalization', rootState.meta.impactRadiusClickId);
                    userAttributes = {
                        ...userAttributes,
                        imp_ClickId: rootState.meta.impactRadiusClickId,
                        imp_ActionTrackerId: '45363',
                        imp_CampaignId: '24486',
                    };
                }

                dispatch('Personalization/ping', {
                    eventName: 'PURCHASE',
                    payload: {
                        source: {
                            pageType: 'order_confirmation',
                            url: window.location.href,
                            urlReferrer: document.referrer,
                            locale: 'en_US',
                        },
                        user: {
                            identities: {
                                accountId: summary.id.replace(/#/g, ''), // same as order.Product.orderId
                            },
                            attributes: userAttributes
                        },
                        order: {
                            Product: {
                                orderId: summary.id.replace(/#/g, ''),
                                totalValue: (rootState.Buyflow.receipt.cartData.promoPriceTotal + rootState.Buyflow.receipt.cartData.taxes).toString(),
                                currency: 'USD',
                                lineItems: getters.salesforceCart,
                            },
                        },
                    },
                }, ROOT)

                // Impact Radius tracking
                const impactUuid = localStorage.getItem('impactuuid');

                // Impact Radius: Push the impactConversion event to the GTM dataLayer
                if (impactUuid) {
                    window.dataLayer.push({
                        event: "impactConversion",
                        impactConversion: {
                            customProfileId: impactUuid,
                            items: getters.impactCart,
                        }
                    });
                }
            }
		}
	},
}
